import * as React from 'react';
import {Alert, Box, Button, Divider, Stack, Typography} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import AppleIcon from '@mui/icons-material/Apple';
import {Capacitor} from '@capacitor/core';
import {useQuery} from '@tanstack/react-query';
import {getSsoProviders, startSso} from '../../services/authService';
import {apiErrorMessage, isNetworkError} from '../../utilities/apiError';
import {providerName} from '../../utilities/sso';
import type {SsoProvider} from '../../contract';

const ICONS: Record<SsoProvider, React.ReactElement> = { google: <GoogleIcon/>, apple: <AppleIcon/> };

// "Continue with Google" and "Continue with Apple", for the providers the server has set up (none shown when it has none). It is the same
// for signing in and signing up: a first sign-in makes the account. The installed Android app is left out: Google does not allow its sign-in
// inside an app's embedded browser.
export function SsoButtons() {
    const [error, setError] = React.useState('');
    const [busy, setBusy] = React.useState<SsoProvider | null>(null);
    const native = Capacitor.isNativePlatform();

    const {data: providers} = useQuery({
        queryKey: ['ssoProviders'],
        queryFn: getSsoProviders,
        enabled: !native,
        staleTime: 5 * 60 * 1000,
        retry: false,
    });

    if (native || !providers?.length) return null;

    const begin = async (provider: SsoProvider) => {
        setError('');
        setBusy(provider);
        try {
            window.location.assign(await startSso(provider));
        } catch (failure) {
            setError(isNetworkError(failure) ? 'Network error. Please try again later.' : apiErrorMessage(failure, `Signing in with ${providerName(provider)} did not start. Please try again.`));
            setBusy(null);
        }
    };

    return (
        <Stack spacing={1.5}>
            <Divider><Typography variant="body2" color="text.secondary">or</Typography></Divider>
            {error && <Alert severity="error">{error}</Alert>}
            {providers.map((provider) => (
                <Button
                    key={provider}
                    variant="outlined"
                    fullWidth
                    startIcon={ICONS[provider]}
                    disabled={busy !== null}
                    onClick={() => void begin(provider)}
                >
                    {busy === provider ? 'Going to ' + providerName(provider) + '…' : `Continue with ${providerName(provider)}`}
                </Button>
            ))}
            <Box/>
        </Stack>
    );
}
